import { createContext, SetStateAction, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import { ContractDetailsTypes } from "../pages/ContractDetails/types";
import {
  createContractService,
  getContractDetailsService,
} from "../services/contractService";
import { dataURItoBlob, parseAPIError } from "../utils/common";
import { showErrorToast, showSuccessToast } from "../utils/toast";

import { AuthContext } from "./auth";
import {
  ContextProviderProps,
  CreateContractPayloadType,
  FormDataTypes,
  HeaderDataTypes,
} from "./types";

const defaultValue = {
  loading: false as Boolean,
  formData: {
    customerSignature: "",
    customerSignatureCanvas: null,
    agentSignatureCanvas: null,
    witnessSignatureCanvas: null,
    agentSignature: "",
    witnessSignature: "",
    customerSignTnC: false,
    agentWitnessSignTnC: false,
    contractDetailsTnC: false,
  } as FormDataTypes,
  handleFormData: (value: FormDataTypes) => {},
  contractDetails: {
    nameOfCustomer: "",
    product: "",
    deposit: "",
    weeklyRechargeAmount: "",
    numberOfWeeks: "",
    totalPrice: "",
    upfrontPrice: "",
    contractStatus: "",
    createdAt: "",
  } as ContractDetailsTypes,
  headerData: { contractStatus: "", createdAt: "" } as HeaderDataTypes,
  isAtBottom: false as Boolean,
  pdfLink: "" as string,
  getContractDetails: (prospectId: string) => {},
  handleDisabledCheckbox: (value: Boolean) => {},
  createContract: (payload: CreateContractPayloadType) => {},
};

export const ContractContext = createContext(defaultValue);

export const ContractProvider = ({ children }: ContextProviderProps) => {
  const navigate = useNavigate();

  const { prospectId, setIsAuthenticated } = useContext(AuthContext);

  const [loading, setLoading] = useState<Boolean>(false);
  const [formData, setFormData] = useState<FormDataTypes>(
    defaultValue.formData
  );
  const [isAtBottom, setIsAtBottom] = useState<Boolean>(false);
  const [contractDetails, setContractDetails] = useState<ContractDetailsTypes>(
    defaultValue.contractDetails
  );
  const [headerData, setHeaderData] = useState<HeaderDataTypes>(
    defaultValue.headerData
  );
  const [pdfLink, setPdfLink] = useState<string>("");

  const getImageBlob = (dataURI: string) => dataURItoBlob(dataURI);

  const getCreatePayload = () => {
    const {
      customerSignature,
      agentSignature,
      witnessSignature,
      agentWitnessSignTnC,
      customerSignatureTime,
      agentSignatureTime,
      coordinates,
      customerPicture,
    } = formData;
    const payload = new FormData();
    const { latitude, longitude } = coordinates;
    payload.append(
      "accepted_terms_conditions",
      agentWitnessSignTnC?.toString()
    );
    payload.append("buyer_signature_time", customerSignatureTime);
    payload.append("agent_signature_time", agentSignatureTime);
    payload.append("latitude", latitude);
    payload.append("longitude", longitude);
    payload.append(
      "buyer_signature_file",
      getImageBlob(customerSignature as string)
    );
    payload.append(
      "agent_signature_file",
      getImageBlob(agentSignature as string)
    );
    payload.append(
      "witness_signature_file",
      getImageBlob(witnessSignature as string)
    );
    payload.append("buyer_image_file", customerPicture);
    return payload;
  };

  const handleOnSuccessContractCreation = () => {
    setIsAuthenticated(false);
    sessionStorage.setItem("isAuthenticated", "false");
    navigate(`/digital-contract/thank-you`);
  };

  const getContractDetails = useMutation({
    onMutate: () => setLoading(true),
    mutationFn: getContractDetailsService,
    onError: (error: any) => showErrorToast(parseAPIError(error)),
    retry: false,
    onSettled: () => setLoading(false),
    onSuccess: (data) => handleOnSuccessContractDetails(data),
  });

  const createContractMutation = useMutation({
    onMutate: () => setLoading(true),
    mutationFn: createContractService,
    onError: (error: any) => showErrorToast(parseAPIError(error)),
    retry: false,
    onSettled: () => setLoading(false),
    onSuccess: () => handleOnSuccessContractCreation(),
  });

  const handleContractCreation = () => {
    createContractMutation.mutate({
      prospectId,
      formData: getCreatePayload(),
    });
  };

  const handleOnSuccessContractDetails = (data: any) => {
    const { data: contractData } = data;
    if (contractData?.pdf_s3_link) {
      setPdfLink(contractData.pdf_s3_link);
      return;
    }
    const { prospect, products, upgrades } = contractData;
    setContractDetails({
      nameOfCustomer: prospect?.customer_name,
      product: products?.product_name,
      deposit: products?.deposit,
      weeklyRechargeAmount: upgrades?.daily_recharge_amount,
      numberOfWeeks: upgrades?.number_of_weeks,
      totalPrice: products?.total_price,
      upfrontPrice: products?.upfront_pricing,
    } as SetStateAction<ContractDetailsTypes>);
    setHeaderData({
      contractStatus: contractData.digital_contract_status,
      createdAt: new Date(products?.created_at).toLocaleString("en-GB", {
        timeZone: "Africa/Nairobi",
        hour12: true,
      }),
    } as HeaderDataTypes);
  };

  const handleFormData = (value: FormDataTypes) => {
    setFormData(value);
  };

  const handleDisabledCheckbox = (value: Boolean) => setIsAtBottom(value);

  const contractContextValue = {
    loading,
    formData,
    headerData,
    contractDetails,
    isAtBottom,
    pdfLink,
    handleDisabledCheckbox,
    handleFormData,
    createContract: handleContractCreation,
    getContractDetails: (prospectId: string) =>
      getContractDetails.mutate(prospectId as string),
  };

  return (
    <ContractContext.Provider value={contractContextValue}>
      {children}
    </ContractContext.Provider>
  );
};
